import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiGet, getApiErrorMessage, type ApiResult } from "@/api";
import { tokenQuery } from "./query-options";

export interface UsageData {
  planId: string;
  capturesToday: number;
  capturesThisMonth: number;
  capturesPerDayLimit: number | null;
  capturesPerMonthLimit: number | null;
  canCapture: boolean;
  upgradeTrigger: string | null;
  upgradeCopy: string | null;
}

/**
 * Turns a `/usage` response into the snapshot the dashboard renders, or throws.
 *
 * Separated from the query so it can be tested: React Query supplies caching, retry and the
 * token; this is the part with an opinion. It deliberately knows nothing about React, the query
 * cache, the token, or how the failure is eventually shown.
 *
 * A failure must NEVER become a snapshot, and `lib/query-state.ts` cannot enforce that from
 * where it sits. `resolveViewState` exists precisely because "no data yet" and "the request
 * failed" render identically once a view reaches for a fallback — but it reads the query's
 * `error`, and a fabricated success has none, so it would answer `ready` and the guard would
 * never fire. The defence has to be here. A cold deploy missing one env var answers
 * `503 missing_config` to every call; inventing a snapshot would show that user as an
 * unlimited Pro who `canCapture`, and the extension would let them capture into nothing.
 */
export function selectUsage(res: ApiResult<{ usage?: UsageData }>): UsageData {
  if (!res.ok) throw new Error(getApiErrorMessage(res, "Usage konnte nicht geladen werden"));
  if (!res.data.usage) throw new Error("Ungültige Usage-Antwort");
  return res.data.usage;
}

/** One progress bar: a capture count and the cap that count is measured against. */
export interface UsageBar {
  readonly period: "day" | "month";
  readonly used: number;
  readonly limit: number | null;
  readonly labelKey: "usage.today" | "usage.thisMonth";
}

/**
 * Pairs each capture count with its own cap and its own label.
 *
 * The pairing IS the bug this guards: the "today" bar drawing `capturesThisMonth` against the
 * DAILY cap shows a Free user's 40 monthly captures as 40 of 10 — a bar past its own end, on
 * the one page whose entire job is telling someone how much of their plan is left. The usage
 * route now renders this list instead of choosing fields inline, so count, cap and label cannot
 * drift apart in JSX.
 */
export function usageBars(usage: UsageData): readonly UsageBar[] {
  return [
    { period: "day", used: usage.capturesToday, limit: usage.capturesPerDayLimit, labelKey: "usage.today" },
    { period: "month", used: usage.capturesThisMonth, limit: usage.capturesPerMonthLimit, labelKey: "usage.thisMonth" },
  ];
}

export function usageQueryOptions(token: string | null) {
  return tokenQuery(["usage"], token, async (t) =>
    selectUsage(await apiGet<{ usage?: UsageData }>("/usage", t)),
  );
}

export function useUsage() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(usageQueryOptions(token));
}
